import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab
} from '@mui/material';
import { formatUKDate, formatUKDateTime, formatUKCurrency } from '../../utils/formatters';
import {
  Assignment as ReturnIcon,
  ShoppingCart as OrderIcon,
  Person as CustomerIcon,
  LocalShipping as ShippingIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createReturnFromOrder } from '../../utils/returnUtils';

const OrderDetails = () => {
  const { platform, id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (id && platform) {
      fetchOrderDetails();
    }
  }, [id, platform]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      // First get the order details
      const orderResponse = await axios.get(`/api/orders/${platform}/${id}`);
      
      let sellercloudOrder = null;
      const promises = [Promise.resolve(orderResponse)];
      
      // If Amazon order has SellerOrderId, fetch Sellercloud data for enhanced customer info
      if (platform === 'amazon' && orderResponse.data?.data?.order?.SellerOrderId) {
        const sellerOrderId = orderResponse.data.data.order.SellerOrderId;
        promises.push(axios.get(`/api/orders/sellercloud/${sellerOrderId}`));
      }
      
      // Get returns
      promises.push(axios.get(`/api/returns?${platform}_order_id=${id}`));
      
      const results = await Promise.allSettled(promises);
      const orderResult = results[0];
      const sellercloudResult = promises.length > 2 ? results[1] : null;
      const returnsResponse = promises.length > 2 ? results[2] : results[1];

      if (orderResult.status === 'fulfilled' && orderResult.value.data.success) {
        const orderData = orderResult.value.data.data;
        
        // Get Sellercloud data if available
        const sellercloudData = sellercloudResult?.status === 'fulfilled' && sellercloudResult.value.data.success ? 
                               sellercloudResult.value.data.data : null;
        
        // Debug logging
        console.log('OrderDetails Debug:');
        console.log('Amazon order data:', orderData.order);
        console.log('Sellercloud data:', sellercloudData);
        console.log('Sellercloud ShippingAddress:', sellercloudData?.order?.ShippingAddress);
        
        // Determine customer name with priority: Sellercloud ShippingAddress > Amazon BuyerInfo > fallback
        let customerName = 'Amazon Customer (Privacy Protected)';
        let customerEmail = 'Not Available';
        let shippingAddress = null;
        
        if (sellercloudData?.order?.ShippingAddress) {
          // Use Sellercloud data for customer info (best option)
          const shipping = sellercloudData.order.ShippingAddress;
          console.log('Using Sellercloud ShippingAddress:', shipping);
          customerName = `${shipping.FirstName || ''} ${shipping.LastName || ''}`.trim() || customerName;
          console.log('Updated customerName from Sellercloud:', customerName);
          customerEmail = sellercloudData.order.OrderDetails?.CustomerEmail || customerEmail;
          shippingAddress = shipping;
        } else if (orderData.order?.BuyerInfo) {
          // Fallback to Amazon data
          customerName = orderData.order.BuyerInfo.BuyerName || customerName;
          customerEmail = orderData.order.BuyerInfo.BuyerEmail || customerEmail;
          shippingAddress = orderData.order.ShippingAddress;
        }
        
        // Set order data with platform info
        setOrder({ 
          ...orderData,
          platform,
          // Extract relevant fields based on platform
          orderId: orderData.order?.AmazonOrderId || orderData.order?.OrderID || orderData.orderId || id,
          orderDate: orderData.order?.PurchaseDate || orderData.order?.OrderDetails?.TimeOfOrder || orderData.orderDate,
          status: orderData.order?.OrderStatus || orderData.order?.StatusCode || orderData.status,
          total: orderData.order?.OrderTotal?.Amount || orderData.order?.TotalInfo?.GrandTotal || orderData.totalAmount,
          currency: orderData.order?.OrderTotal?.CurrencyCode || orderData.order?.OrderCurrencyCode || 'GBP',
          // Customer info from cross-reference
          customerName,
          customerEmail,
          // Shipping info
          shippingAddress,
          fulfillmentChannel: orderData.order?.FulfillmentChannel,
          isPrime: orderData.order?.IsPrime,
          salesChannel: orderData.order?.SalesChannel,
          shipServiceLevel: orderData.order?.ShipServiceLevel,
          // Additional Amazon-specific info
          sellerOrderId: orderData.order?.SellerOrderId
        });
        
        // Set order items
        setOrderItems(orderData.items || []);
      } else {
        throw new Error('Failed to load order details');
      }

      if (returnsResponse.status === 'fulfilled' && returnsResponse.value.data.success) {
        setReturns(returnsResponse.value.data.data || []);
      }

    } catch (error) {
      console.error('Error fetching order details:', error);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReturn = () => {
    if (order) {
      createReturnFromOrder(navigate, order, platform);
    }
  };

  const formatCurrency = formatUKCurrency;

  const getPlatformColor = (platform) => {
    const colors = {
      amazon: 'primary',
      sellercloud: 'secondary',
      ebay: 'warning'
    };
    return colors[platform] || 'default';
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower?.includes('shipped') || statusLower?.includes('delivered')) return 'success';
    if (statusLower?.includes('pending') || statusLower?.includes('processing')) return 'warning';
    if (statusLower?.includes('cancelled') || statusLower?.includes('refunded')) return 'error';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Order not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <img 
            src="https://www.xs-stock.co.uk/cdn/shop/files/XS_NEW_LOGO_2ac57727-dd78-4de8-bd11-0c0bb84a6858.png?v=1738612353&width=400" 
            alt="XS Stock"
            style={{
              height: '32px',
              marginRight: '12px',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}
          />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A365D' }}>
              Order #{order.orderId || order.id}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Chip 
                label={platform.toUpperCase()} 
                color={getPlatformColor(platform)}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <Chip 
                label={order.status || order.orderStatus || 'Unknown'} 
                color={getStatusColor(order.status || order.orderStatus)}
                size="small"
              />
            </Box>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<ReturnIcon />}
          onClick={handleCreateReturn}
          size="large"
        >
          Create Return
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Order Summary Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <OrderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Order Summary
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Order Date"
                    secondary={formatUKDateTime(order.orderDate)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Total Amount"
                    secondary={order.total ? `${formatCurrency(order.total)} ${order.currency || 'GBP'}` : '£0.00'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Items Count"
                    secondary={`${orderItems.length} items`}
                  />
                </ListItem>
                {order.fulfillmentChannel && (
                  <ListItem>
                    <ListItemText 
                      primary="Fulfillment"
                      secondary={order.fulfillmentChannel}
                    />
                  </ListItem>
                )}
                {order.isPrime && (
                  <ListItem>
                    <ListItemText 
                      primary="Prime Order"
                      secondary="Yes"
                    />
                  </ListItem>
                )}
                {order.salesChannel && (
                  <ListItem>
                    <ListItemText 
                      primary="Sales Channel"
                      secondary={order.salesChannel}
                    />
                  </ListItem>
                )}
                {order.shipServiceLevel && (
                  <ListItem>
                    <ListItemText 
                      primary="Ship Service"
                      secondary={order.shipServiceLevel}
                    />
                  </ListItem>
                )}
                {order.sellerOrderId && (
                  <ListItem>
                    <ListItemText 
                      primary="Seller Order ID"
                      secondary={order.sellerOrderId}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CustomerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Customer Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Name"
                    secondary={order.customerName || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Email"
                    secondary={order.customerEmail === 'N/A' ? 'Not Available' : order.customerEmail}
                  />
                </ListItem>
                {order.shippingAddress && (
                  <ListItem>
                    <ListItemText 
                      primary="Shipping Address"
                      secondary={
                        <Box component="div" sx={{ whiteSpace: 'pre-line' }}>
                          {order.shippingAddress.FirstName && order.shippingAddress.LastName ? 
                            `${order.shippingAddress.FirstName} ${order.shippingAddress.LastName}\n` : ''}
                          {order.shippingAddress.StreetLine1 && `${order.shippingAddress.StreetLine1}\n`}
                          {order.shippingAddress.StreetLine2 && `${order.shippingAddress.StreetLine2}\n`}
                          {order.shippingAddress.City && `${order.shippingAddress.City}, `}
                          {order.shippingAddress.PostalCode || order.shippingAddress.postalCode}
                          {order.shippingAddress.CountryName && `\n${order.shippingAddress.CountryName}`}
                          {order.shippingAddress.PhoneNumber && `\nPhone: ${order.shippingAddress.PhoneNumber}`}
                        </Box>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Returns History */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Returns History
              </Typography>
              {returns.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No returns for this order
                </Typography>
              ) : (
                <List dense>
                  {returns.map((returnItem, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Return #${returnItem.return_number}`}
                        secondary={
                          <Box>
                            <Typography variant="caption">
                              {returnItem.return_reason} • {formatCurrency(returnItem.refund_amount)}
                            </Typography>
                            <br />
                            <Chip 
                              label={returnItem.return_status} 
                              size="small" 
                              color={returnItem.return_status === 'completed' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Order Items */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Items ({orderItems.length})
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU/ASIN</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderItems.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.Title || item.productName || item.title || item.itemName || 'Unknown Product'}
                          </Typography>
                          {item.ASIN && (
                            <Typography variant="caption" color="text.secondary">
                              ASIN: {item.ASIN}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.SellerSKU || item.sku || item.asin || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {item.QuantityOrdered || item.quantity || item.quantityOrdered || 1}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(item.ItemPrice?.Amount || item.unitPrice || item.price || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatCurrency((item.ItemPrice?.Amount || item.unitPrice || item.price || 0) * (item.QuantityOrdered || item.quantity || item.quantityOrdered || 1))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.QuantityShipped > 0 ? 'Shipped' : 'Ordered'} 
                            size="small"
                            color={item.QuantityShipped > 0 ? 'success' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetails;